import { Box, SxProps, Typography } from '@mui/material';
import { Component, ErrorInfo, FC, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from 'system/atoms';
import { Paths } from 'services/navigation/paths';
import { logger } from 'core/utils/logger';

const container: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  height: '100%',
  textAlign: 'center',
  px: 4
};

type Props = {
  children: ReactNode;
  /**
   * Changing this value clears a caught error. We pass the route pathname so
   * navigating away from a broken page recovers without a manual reload.
   */
  resetKey: string;
};

type State = {
  hasError: boolean;
};

/**
 * Catches render-time errors anywhere below it and shows a fallback instead of
 * unmounting the whole React tree (which previously left users on a blank
 * white screen with no way to recover).
 */
class ErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error(error, {
      type: 'react_render_error',
      componentStack: errorInfo.componentStack
    });
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box sx={container}>
          <Typography variant="h5">Something went wrong</Typography>
          <Typography variant="body1" color="text.secondary">
            This page failed to load. Please go back and try again, or contact
            support if the problem persists.
          </Typography>
          <Button
            variant="primary"
            onClick={() => window.location.assign(Paths.HOME)}
          >
            Go to homepage
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();

  return (
    <ErrorBoundaryInner resetKey={location.pathname}>
      {children}
    </ErrorBoundaryInner>
  );
};
